// Auth Dialog Component
import { AuthManager } from "../../services/auth-manager";
import { I18nManager } from "../../services/i18n-manager";
import { DialogManager } from "./dialog-manager";

export class AuthDialog {
  private authManager: AuthManager;
  private i18nManager: I18nManager;
  private dialogManager: DialogManager;
  private dialog: HTMLElement | null = null;
  private currentTab: string = "login";
  private loginForm: HTMLFormElement | null = null;
  private registerForm: HTMLFormElement | null = null;

  constructor(
    authManager: AuthManager,
    i18nManager: I18nManager,
    dialogManager: DialogManager
  ) {
    this.authManager = authManager;
    this.i18nManager = i18nManager;
    this.dialogManager = dialogManager;
  }

  init(): void {
    this.dialog = document.getElementById("auth-dialog");
    if (!this.dialog) return;

    this.loginForm = document.getElementById("login-form") as HTMLFormElement;
    this.registerForm = document.getElementById(
      "register-form"
    ) as HTMLFormElement;

    // Set initial tab state to show login form
    this.switchTab("login");

    this.setupEventListeners();
    this.updateTranslations();
  }

  private setupEventListeners(): void {
    if (!this.dialog) return;

    // Tab switching
    const tabs = this.dialog.querySelectorAll(".auth-tab");
    tabs.forEach((tab) => {
      tab.addEventListener("click", (e) => {
        e.preventDefault();
        const tabName = tab.getAttribute("data-tab");
        if (tabName) {
          this.switchTab(tabName);
        }
      });
    });

    // Login form submission
    if (this.loginForm) {
      this.loginForm.addEventListener("submit", async (e) => {
        e.preventDefault();
        await this.handleLogin();
      });
    }

    // Register form submission
    if (this.registerForm) {
      this.registerForm.addEventListener("submit", async (e) => {
        e.preventDefault();
        await this.handleRegister();
      });
    }

    // Language changes
    this.i18nManager.onLanguageChange(() => {
      this.updateTranslations();
    });

    // Dialog open event
    this.dialog.addEventListener("dialog:open", () => {
      this.switchTab("login");
      this.clearForms();
    });
  }

  private switchTab(tab: string): void {
    this.currentTab = tab;

    // Update tab buttons
    const tabs = this.dialog?.querySelectorAll(".auth-tab");
    tabs?.forEach((t) => {
      if (t.getAttribute("data-tab") === tab) {
        t.classList.add("active");
      } else {
        t.classList.remove("active");
      }
    });

    // Show/hide forms
    if (this.loginForm && this.registerForm) {
      if (tab === "login") {
        this.loginForm.classList.remove("hidden");
        this.registerForm.classList.add("hidden");
      } else {
        this.loginForm.classList.add("hidden");
        this.registerForm.classList.remove("hidden");
      }
    }
  }

  private async handleLogin(): Promise<void> {
    if (!this.loginForm) return;

    const email = (
      this.loginForm.querySelector("#login-email") as HTMLInputElement
    )?.value;
    const password = (
      this.loginForm.querySelector("#login-password") as HTMLInputElement
    )?.value;

    if (!email || !password) {
      this.showError(this.i18nManager.t("error.validation"));
      return;
    }

    const submitButton = this.loginForm.querySelector(
      ".auth-submit"
    ) as HTMLButtonElement;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = this.i18nManager.t("common.loading");
    }

    try {
      const result = await this.authManager.signIn(email, password);
      if (result.success) {
        this.showSuccess(this.i18nManager.t("auth.login_success"));
        setTimeout(() => {
          this.dialogManager.closeDialog();
        }, 1000);
      } else {
        // Use translation key if available, otherwise fallback to error message
        const errorKey = result.errorCode 
          ? this.authManager.getErrorMessageKey(result.errorCode)
          : "auth.error.generic";
        const translatedError = this.i18nManager.t(errorKey);
        const errorMessage = translatedError !== errorKey 
          ? translatedError 
          : (result.error || this.i18nManager.t("error.unauthorized"));
        this.showError(errorMessage);
      }
    } catch (error) {
      console.error("Login error:", error);
      this.showError(this.i18nManager.t("error.server") || "Server error. Please try again later.");
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = this.i18nManager.t("auth.sign_in");
      }
    }
  }

  private async handleRegister(): Promise<void> {
    if (!this.registerForm) return;

    const firstName = (
      this.registerForm.querySelector(
        "#register-first-name"
      ) as HTMLInputElement
    )?.value;
    const lastName = (
      this.registerForm.querySelector("#register-last-name") as HTMLInputElement
    )?.value;
    const email = (
      this.registerForm.querySelector("#register-email") as HTMLInputElement
    )?.value;
    const password = (
      this.registerForm.querySelector("#register-password") as HTMLInputElement
    )?.value;
    const address = (
      this.registerForm.querySelector("#register-address") as HTMLInputElement
    )?.value;
    const city = (
      this.registerForm.querySelector("#register-city") as HTMLInputElement
    )?.value;
    const postal = (
      this.registerForm.querySelector("#register-postal") as HTMLInputElement
    )?.value;
    const country = (
      this.registerForm.querySelector("#register-country") as HTMLSelectElement
    )?.value;

    if (
      !firstName ||
      !lastName ||
      !email ||
      !password ||
      !address ||
      !city ||
      !postal ||
      !country
    ) {
      this.showError(this.i18nManager.t("error.validation"));
      return;
    }

    if (password.length < 6) {
      this.showError("Password must be at least 6 characters");
      return;
    }

    const submitButton = this.registerForm.querySelector(
      ".auth-submit"
    ) as HTMLButtonElement;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = this.i18nManager.t("common.loading");
    }

    try {
      const fullName = `${firstName} ${lastName}`;
      const customerData = {
        firstName,
        lastName,
        email,
        address: {
          line1: address,
          city,
          postal_code: postal,
          country,
        },
      };

      const result = await this.authManager.createAccount(
        email,
        password,
        fullName,
        customerData
      );
      if (result.success) {
        this.showSuccess(this.i18nManager.t("auth.register_success"));
        setTimeout(() => {
          this.dialogManager.closeDialog();
        }, 1000);
      } else {
        this.showError(result.error || this.i18nManager.t("error.server"));
      }
    } catch (error) {
      console.error("Register error:", error);
      this.showError(this.i18nManager.t("error.server"));
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = this.i18nManager.t("auth.create_account");
      }
    }
  }

  private clearForms(): void {
    if (this.loginForm) {
      this.loginForm.reset();
    }
    if (this.registerForm) {
      this.registerForm.reset();
    }
  }

  private updateTranslations(): void {
    // Update dialog title
    const title = this.dialog?.querySelector(".dialog-header h2");
    if (title) {
      title.textContent = this.i18nManager.t("auth.dialog_title");
    }

    // Update tab labels
    const loginTab = this.dialog?.querySelector('.auth-tab[data-tab="login"]');
    if (loginTab) {
      loginTab.textContent = this.i18nManager.t("auth.login_tab");
    }

    const registerTab = this.dialog?.querySelector(
      '.auth-tab[data-tab="register"]'
    );
    if (registerTab) {
      registerTab.textContent = this.i18nManager.t("auth.register_tab");
    }

    // Update form labels
    const loginEmailLabel = this.dialog?.querySelector(
      'label[for="login-email"]'
    );
    if (loginEmailLabel) {
      loginEmailLabel.textContent = this.i18nManager.t("auth.email");
    }

    const loginPasswordLabel = this.dialog?.querySelector(
      'label[for="login-password"]'
    );
    if (loginPasswordLabel) {
      loginPasswordLabel.textContent = this.i18nManager.t("auth.password");
    }

    const registerFirstNameLabel = this.dialog?.querySelector(
      'label[for="register-first-name"]'
    );
    if (registerFirstNameLabel) {
      registerFirstNameLabel.textContent =
        this.i18nManager.t("auth.first_name") || "First Name";
    }

    const registerLastNameLabel = this.dialog?.querySelector(
      'label[for="register-last-name"]'
    );
    if (registerLastNameLabel) {
      registerLastNameLabel.textContent =
        this.i18nManager.t("auth.last_name") || "Last Name";
    }

    const registerEmailLabel = this.dialog?.querySelector(
      'label[for="register-email"]'
    );
    if (registerEmailLabel) {
      registerEmailLabel.textContent = this.i18nManager.t("auth.email");
    }

    const registerPasswordLabel = this.dialog?.querySelector(
      'label[for="register-password"]'
    );
    if (registerPasswordLabel) {
      registerPasswordLabel.textContent = this.i18nManager.t("auth.password");
    }

    // Update shipping address heading
    const shippingAddressHeading = this.registerForm?.querySelector("h4");
    if (shippingAddressHeading) {
      shippingAddressHeading.textContent = this.i18nManager.t(
        "auth.shipping_address"
      );
    }

    // Update address field labels
    const registerAddressLabel = this.dialog?.querySelector(
      'label[for="register-address"]'
    );
    if (registerAddressLabel) {
      registerAddressLabel.textContent = this.i18nManager.t("auth.address");
    }

    const registerCityLabel = this.dialog?.querySelector(
      'label[for="register-city"]'
    );
    if (registerCityLabel) {
      registerCityLabel.textContent = this.i18nManager.t("auth.city");
    }

    const registerPostalLabel = this.dialog?.querySelector(
      'label[for="register-postal"]'
    );
    if (registerPostalLabel) {
      registerPostalLabel.textContent = this.i18nManager.t("auth.postal_code");
    }

    const registerCountryLabel = this.dialog?.querySelector(
      'label[for="register-country"]'
    );
    if (registerCountryLabel) {
      registerCountryLabel.textContent = this.i18nManager.t("auth.country");
    }

    // Update submit buttons
    const loginSubmit = this.loginForm?.querySelector(".auth-submit");
    if (loginSubmit) {
      loginSubmit.textContent = this.i18nManager.t("auth.sign_in");
    }

    const registerSubmit = this.registerForm?.querySelector(".auth-submit");
    if (registerSubmit) {
      registerSubmit.textContent = this.i18nManager.t("auth.create_account");
    }
  }

  private showError(message: string): void {
    // Use DialogManager's showNotification for consistency
    this.dialogManager.showNotification(message, "error");
  }

  private showSuccess(message: string): void {
    // Use DialogManager's showNotification for consistency
    this.dialogManager.showNotification(message, "success");
  }
}
